import React, { useState } from "react";
import { api, CHAIN_META, fmtUSD, shortAddr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Loader2 } from "lucide-react";

export default function DonorDashboard() {
  const [wallet, setWallet] = useState("");
  const [data, setData] = useState(null);
  const [airdrop, setAirdrop] = useState(null);
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState(false);

  const load = async () => {
    if (!wallet.trim()) return;
    setLoading(true);
    try {
      const [d, a] = await Promise.all([
        api.get(`/donations/wallet/${encodeURIComponent(wallet.trim())}`),
        api.get(`/airdrops/wallet/${encodeURIComponent(wallet.trim())}`),
      ]);
      setData(d.data);
      setAirdrop(a.data);
      setTouched(true);
    } catch { setData({ donations: [], total_usd: 0, donation_count: 0, by_chain: {}, by_period: {} }); setAirdrop({ total_quodust: 0, entries: [] }); setTouched(true); }
    finally { setLoading(false); }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10">
      <div className="text-xs uppercase tracking-widest text-[#FFC940]">donor dashboard</div>
      <h1 className="mt-2 font-display text-4xl sm:text-5xl font-black">how big is your <span className="ember-text">bag of ashes</span>?</h1>

      <div className="mt-6 flex flex-col sm:flex-row gap-2">
        <Input
          data-testid="donor-wallet-input"
          value={wallet}
          onChange={(e) => setWallet(e.target.value)}
          placeholder="paste your wallet to see your donation history"
          className="bg-[#141008] border-[#3A2E1C]"
        />
        <Button data-testid="donor-wallet-lookup" onClick={load} disabled={loading} className="bg-[#FFAB00] hover:bg-[#FFC940] text-[#0A0906] font-semibold min-w-[140px]">
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
          look up
        </Button>
      </div>

      {touched && data && (
        <div className="mt-8 grid lg:grid-cols-2 gap-4">
          <Card label="Lifetime donated" value={fmtUSD(data.total_usd)} hint={`${data.donation_count} tx`} />
          <Card label="$QUODUST earned" value={Number(airdrop?.total_quodust || 0).toLocaleString()} hint={`${airdrop?.entries?.length || 0} snapshots`} />
        </div>
      )}

      {touched && data && (
        <div className="mt-6 grid lg:grid-cols-2 gap-4">
          <section className="rounded-xl border border-[#3A2E1C] bg-[#141008] p-5">
            <div className="font-display text-lg mb-3">breakdown by chain</div>
            {Object.keys(data.by_chain || {}).length === 0 ? (
              <div className="text-sm text-stone-500">no donations yet · plant a coin</div>
            ) : (
              <div className="space-y-2">
                {Object.entries(data.by_chain).map(([k, v]) => {
                  const meta = CHAIN_META[k] || { name: k, color: "#FFC940" };
                  const pct = data.total_usd > 0 ? Math.min(100, (v / data.total_usd) * 100) : 0;
                  return (
                    <div key={k}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: meta.color }} />{meta.name}</span>
                        <span className="mono text-stone-400">{fmtUSD(v)}</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-[#1E1810] overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: meta.color }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          <section className="rounded-xl border border-[#3A2E1C] bg-[#141008] p-5">
            <div className="font-display text-lg mb-3">airdrop ledger</div>
            {airdrop?.entries?.length ? (
              <div className="space-y-2">
                {airdrop.entries.map((e, i) => (
                  <div key={i} className="flex items-center justify-between rounded-md border border-[#3A2E1C] bg-[#0A0906] p-3 text-sm">
                    <div>
                      <div className="font-medium">{e.period_label}</div>
                      <div className="text-xs text-stone-500">share {(e.share * 100).toFixed(4)}% · {fmtUSD(e.usd_donated)}</div>
                    </div>
                    <div className="font-display text-lg ember-text">{Number(e.quodust_allocated).toLocaleString()}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-stone-500">no snapshot entries yet · wait for month-end</div>
            )}
          </section>
        </div>
      )}

      {touched && data && (
        <section className="mt-6 rounded-xl border border-[#3A2E1C] bg-[#141008] p-5">
          <div className="font-display text-lg mb-3">your tx log</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-stone-500">
                <tr><th className="text-left py-2">when</th><th className="text-left">token</th><th className="text-left">chain</th><th className="text-right">amount</th><th className="text-right">usd</th><th className="text-left pl-4">tx</th></tr>
              </thead>
              <tbody>
                {(data.donations || []).map((d) => (
                  <tr key={d.id} className="border-t border-[#1E1810]" data-testid="donor-tx-row">
                    <td className="py-2 text-stone-400 mono text-xs">{new Date(d.created_at).toLocaleDateString()}</td>
                    <td>${d.token_symbol}</td>
                    <td className="text-stone-400">{(CHAIN_META[d.chain] || {}).name || d.chain}</td>
                    <td className="text-right mono">{Number(d.amount).toLocaleString()}</td>
                    <td className="text-right mono text-[#FFD54F]">{fmtUSD(d.usd_value)}</td>
                    <td className="pl-4 mono text-xs text-stone-500">{shortAddr(d.tx_hash)}</td>
                  </tr>
                ))}
                {(data.donations || []).length === 0 && (
                  <tr><td colSpan="6" className="py-6 text-center text-stone-500">no tx yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

function Card({ label, value, hint }) {
  return (
    <div className="rounded-xl border border-[#3A2E1C] bg-[#141008] p-5">
      <div className="text-xs uppercase tracking-widest text-stone-500">{label}</div>
      <div className="mt-2 font-display text-3xl font-bold ember-text">{value}</div>
      {hint && <div className="mt-1 text-xs text-stone-500">{hint}</div>}
    </div>
  );
}
